import { useMemo } from "react";
import { fmtEUR } from "@/lib/api";
import { PROJECT_AREAS } from "@/lib/project_areas";

/**
 * ProjectOverview · Portada del proyecto (Mockup D #s2).
 *
 * Aterriza por defecto al abrir un proyecto. Sustituye a la antigua tab
 * "Dossier" como destino inicial. Estructura:
 *
 *  · Lista de áreas del proyecto ("¿En qué quieres trabajar hoy?") con
 *    descripción dinámica, indicador de progreso y CTA → cambia de tab vía
 *    `onGoTab(key)`.
 *  · Tareas / notas pendientes (placeholder — se desarrollarán como tab Notas).
 *
 * Las áreas son las definidas en `lib/project_areas.js`. El icono ★ del editor
 * de guion se destaca como atajo principal.
 */

export default function ProjectOverview({ project, onGoTab, totals, finTotal, coverage }) {
  const stats = useMemo(() => buildAreaStats(project, totals, finTotal, coverage), [project, totals, finTotal, coverage]);

  return (
    <section className="mx-auto max-w-[1480px] px-6 py-10 sm:px-10" data-testid="project-overview">
      <div className="grid gap-10 lg:grid-cols-[1.7fr_1fr]">
        {/* Columna principal */}
        <div>
          <div>
            <p className="gb-eyebrow">Elementos del proyecto · {project.project_type}</p>
            <h2 className="mt-1 gb-display text-2xl sm:text-3xl">¿En qué quieres trabajar hoy?</h2>
            <p className="mt-2 max-w-2xl text-sm text-gb-muted">
              Cada bloque es una pieza del proyecto. Pulsa para ir directamente a esa zona — también puedes
              navegar por la barra lateral.
            </p>
          </div>

          <div className="mt-6 overflow-hidden rounded-2xl border border-gb-paper bg-gb-surface">
            {PROJECT_AREAS.filter((a) => a.key !== "overview").map((area, idx, arr) => {
              const s = stats[area.key] || {};
              const isLast = idx === arr.length - 1;
              return (
                <button
                  key={area.key}
                  onClick={() => onGoTab(area.key)}
                  data-testid={`overview-row-${area.key}`}
                  className={`group flex w-full items-center gap-4 px-5 py-4 text-left transition hover:bg-gb-paper-soft/60 ${
                    !isLast ? "border-b border-gb-paper" : ""
                  } ${area.accent ? "bg-gb-paper-soft/30" : ""}`}
                >
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-base ${
                    area.accent ? "bg-gb-red text-white" : "bg-gb-paper-soft text-gb-black"
                  }`}>
                    {area.icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-serif text-lg leading-tight text-gb-black">
                      {area.label}
                      {area.accent && (
                        <span className="ml-2 inline-block rounded-full bg-gb-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-gb-red">
                          Destacado
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-gb-muted">{s.description || area.description}</p>
                  </div>
                  {s.progress != null && (
                    <div className="hidden w-32 shrink-0 md:block">
                      <div className="h-1 overflow-hidden rounded-full bg-gb-paper">
                        <div className={`h-full ${s.ok ? "bg-emerald-600" : "bg-gb-red"}`}
                             style={{ width: `${Math.min(100, s.progress)}%` }} />
                      </div>
                    </div>
                  )}
                  <span className="hidden w-28 shrink-0 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-gb-muted sm:block">
                    {s.status || ""}
                  </span>
                  <span className="shrink-0 text-gb-muted transition group-hover:translate-x-0.5 group-hover:text-gb-red">
                    →
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Columna lateral */}
        <aside className="space-y-5">
          <div className="rounded-2xl border border-gb-paper bg-gb-surface p-5">
            <p className="gb-eyebrow">Resumen</p>
            <h3 className="mt-1 gb-display text-xl">Visión rápida</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <SummaryRow label="Presupuesto" value={fmtEUR(totals.grand)} />
              <SummaryRow label="Financiación" value={fmtEUR(finTotal)} />
              <SummaryRow label="Cobertura"
                          value={`${coverage.toFixed(1)}%`}
                          accent={coverage >= 100 ? "ok" : "warn"} />
              <SummaryRow label="Días rodaje" value={project.shoot_days || "—"} />
              <SummaryRow label="Localizaciones" value={project.locations || "—"} />
              <SummaryRow label="Director/a" value={project.director || "—"} />
              <SummaryRow label="Productora" value={project.producer || "—"} />
            </ul>
          </div>

          <div className="rounded-2xl border border-gb-paper bg-gb-surface p-5">
            <p className="gb-eyebrow">Atajo</p>
            <h3 className="mt-1 gb-display text-xl">Continuar la escritura</h3>
            <p className="mt-2 text-sm text-gb-muted">
              El editor de guion conserva tu última posición, navegador de escenas y panel IA. Ábrelo para
              seguir donde lo dejaste.
            </p>
            <button
              onClick={() => onGoTab("screenplay")}
              data-testid="overview-go-screenplay"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-gb-red px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover-gb-red"
            >
              ✦ Abrir editor de guion
            </button>
          </div>
        </aside>
      </div>
    </section>
  );
}

const SummaryRow = ({ label, value, accent }) => (
  <li className="flex items-center justify-between gap-3">
    <span className="text-[11px] uppercase tracking-[0.2em] text-gb-muted">{label}</span>
    <span className={`truncate text-right text-sm font-medium ${
      accent === "ok" ? "text-emerald-700" : accent === "warn" ? "text-gb-red" : "text-gb-black"
    }`}>
      {value}
    </span>
  </li>
);

function buildAreaStats(project, totals, finTotal, coverage) {
  const dossierSections = (project?.dossier?.sections || []).filter((s) => s.content || (s.entries || []).length > 0).length;
  const dossierTotal = (project?.dossier?.sections || []).length;
  const screenplay = project?.screenplay || {};
  const screenplayScenes = (screenplay.elements || []).filter((e) => e.type === "scene_heading").length;
  const characters = (screenplay.characters || []).length;
  const casting = (project?.casting?.characters || []).length;
  const scriptUploaded = !!project?.script?.uploaded_script?.filename;
  const cal = (project?.calendar?.blocks || []).length;
  const finCount = (project?.financing || []).length;
  const pipelineCount = (project?.pipeline_entries?.length) || (project?.pipeline?.length) || 0;

  const warnings = totals?.warnings?.length || 0;

  return {
    dossier: {
      description: `${dossierSections}/${dossierTotal} secciones con contenido`,
      progress: dossierTotal ? (dossierSections / dossierTotal) * 100 : 0,
      ok: dossierTotal > 0 && dossierSections >= dossierTotal,
      status: dossierTotal && dossierSections >= dossierTotal ? "completo" : "en curso",
    },
    screenplay: {
      description: `${screenplayScenes} escenas · ${characters} personajes en matriz`,
      progress: screenplayScenes > 0 ? Math.min(100, screenplayScenes * 4) : 0,
      ok: screenplayScenes > 0,
      status: screenplayScenes > 0 ? "activo" : "vacío",
    },
    script: {
      description: scriptUploaded
        ? `Guion subido · ${(project?.script?.uploaded_script?.char_count || 0).toLocaleString("es-ES")} caracteres`
        : "Sube tu PDF para generar escaleta o tratamiento",
      progress: scriptUploaded ? 100 : 0,
      ok: scriptUploaded,
      status: scriptUploaded ? "subido" : "pendiente",
    },
    casting: {
      description: casting > 0 ? `${casting} personajes analizados con IA` : "Sube el guion y analiza el reparto",
      progress: casting > 0 ? Math.min(100, casting * 8) : 0,
      ok: casting > 0,
      status: casting > 0 ? `${casting} fichas` : "por trabajar",
    },
    calendar: {
      description: cal > 0 ? `${cal} bloques planificados` : "Sin planificación todavía",
      progress: cal > 0 ? Math.min(100, cal * 10) : 0,
      ok: cal > 0,
      status: cal > 0 ? "planificado" : "vacío",
    },
    budget: {
      description: warnings > 0
        ? `${fmtEUR(totals.grand)} · ${warnings} aviso${warnings > 1 ? "s" : ""} ICAA`
        : `${fmtEUR(totals.grand)} · Cap.11 y Cap.12 dentro de límite`,
      progress: totals.grand > 0 ? 100 : 0,
      ok: warnings === 0 && totals.grand > 0,
      status: warnings === 0 && totals.grand > 0 ? "cumple" : warnings > 0 ? "ajustar" : "vacío",
    },
    financing: {
      description: `${fmtEUR(finTotal)} captados de ${fmtEUR(totals.grand)} · ${finCount} fuentes`,
      progress: coverage,
      ok: coverage >= 100,
      status: `${coverage.toFixed(0)}%`,
    },
    pipeline: {
      description: pipelineCount > 0 ? `${pipelineCount} organizaciones en seguimiento` : "Añade fundaciones y broadcasters",
      progress: pipelineCount > 0 ? Math.min(100, pipelineCount * 8) : 0,
      ok: pipelineCount > 0,
      status: pipelineCount > 0 ? `${pipelineCount} orgs` : "vacío",
    },
  };
}
